import { useEffect, useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import type { Canon, Profile, Work } from '../../core/types.ts'
import { deriveCategoryId } from '../../core/categorize.ts'
import { api } from '../lib/api.ts'
import { Shell } from '../components/Shell.tsx'
import { Button } from '../components/ui/button.tsx'
import { Input, Select } from '../components/ui/input.tsx'
import { Alert } from '../components/ui/alert.tsx'
import { plural } from '../lib/utils.ts'
import type { Theme } from '../lib/theme.ts'
import { useAdminMode } from '../lib/useAdminMode.tsx'
import { AdminNotice } from '../components/AdminNotice.tsx'
import { EditWorkDialog } from '../components/EditWorkDialog.tsx'

/**
 * Oprava faktů o díle po importu — jiná věc než Doporučená (názor o kvalitě)
 * nebo výběr do seznamu. Tady se opravují chyby, ne vybírá.
 */
export function KanonPage({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const [canon, setCanon] = useState<Canon | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Work | null>(null)
  const { locked } = useAdminMode()

  useEffect(() => {
    void (async () => {
      try {
        const canons = await api.canons()
        if (!canons[0]) return setError('Není žádný kánon — nejdřív ho naimportuj.')
        const [c, profiles] = await Promise.all([api.canon(canons[0].id), api.profiles()])
        setCanon(c)
        setProfile(profiles[0] ?? null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Načtení se nepodařilo')
      }
    })()
  }, [])

  const visible = useMemo(() => {
    if (!canon || !profile) return []
    const q = query.trim().toLowerCase()
    return canon.works.filter((w) => {
      if (q && !`${w.author ?? ''} ${w.title}`.toLowerCase().includes(q)) return false
      if (category && deriveCategoryId(w, profile) !== category) return false
      return true
    })
  }, [canon, profile, query, category])

  if (error && !canon) {
    return <Shell theme={theme} onToggleTheme={onToggleTheme} title="Kánon"><Alert variant="destructive">{error}</Alert></Shell>
  }
  if (!canon || !profile) {
    return <Shell theme={theme} onToggleTheme={onToggleTheme} title="Kánon"><p className="text-[var(--muted-foreground)]">Načítám…</p></Shell>
  }

  const formLabel = (w: Work) => profile.forms.find((f) => f.id === w.formId)?.label ?? w.formId

  return (
    <Shell
      theme={theme}
      onToggleTheme={onToggleTheme}
      title="Kánon"
      subtitle="Oprava faktů o díle — autor, forma, rok, původ. Změna se projeví ve všech seznamech z tohoto kánonu."
    >
      <AdminNotice what="Kánon" />
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Hledat autora nebo dílo…" aria-label="Hledat" className="w-full sm:w-64" />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filtr kategorie">
          <option value="">Všechny kategorie</option>
          {[...profile.categories].sort((a, b) => a.order - b.order).map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)]">
        <div className="max-h-[min(44rem,calc(100dvh-16rem))] overflow-auto">
          <table className="w-full min-w-[34rem] text-left">
            <caption className="sr-only">Kánon s možností opravit fakta o díle</caption>
            <thead className="sticky top-0 z-10 bg-[var(--card)]">
              <tr className="border-b border-[var(--border)] text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
                <th scope="col" className="px-4 py-2.5 font-semibold">Autor · Dílo</th>
                <th scope="col" className="w-24 px-3 py-2.5 font-semibold">Forma</th>
                <th scope="col" className="w-20 px-3 py-2.5 font-semibold">Rok</th>
                <th scope="col" className="w-28 px-4 py-2.5 text-right font-semibold">Úprava</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((w) => (
                <tr key={w.id} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-4 py-[var(--space-3)]">
                    <div className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
                      {w.author ?? 'bez autora'}
                    </div>
                    <div className="font-serif text-[length:var(--text-lg)] font-medium tracking-[var(--tracking-title)]">{w.title}</div>
                  </td>
                  <td className="px-3 py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--muted-foreground)]">{formLabel(w)}</td>
                  <td className="px-3 py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--muted-foreground)]">{w.firstPublished ?? '—'}</td>
                  <td className="px-4 py-[var(--space-3)] text-right">
                    <Button size="sm" variant="outline" disabled={locked} onClick={() => setEditing(w)} aria-label={`Upravit dílo: ${w.title}`}>
                      <Pencil size={14} aria-hidden /> Upravit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-2 text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
        Zobrazeno {plural(visible.length, 'dílo', 'díla', 'děl')} ze {canon.works.length}.
      </p>

      {editing && (
        <EditWorkDialog
          canonId={canon.id}
          work={editing}
          profile={profile}
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          onSaved={(updated) => {
            setCanon((prev) => (prev ? { ...prev, works: prev.works.map((w) => (w.id === updated.id ? updated : w)) } : prev))
            setEditing(null)
          }}
        />
      )}
    </Shell>
  )
}
